/* hom: hom-a11y.js (House of Maryam layer) - accessibility and motion helpers.
   Loaded with defer from snippets/hom-head.liquid, after swiper-bundle.min.js, global.js and hom-base.js.
   Settings: <script type="application/json" id="hom-a11y-config"> in snippets/hom-body-seo.liquid.
   Styles: assets/hom-a11y.css. Everything here only enhances; the page works without it.

   1. Keyboard focus: html.hom-kbd-only (setting) + html.hom-kbd while the keyboard is used
      (hom-base.js sets it on Tab; arrow keys, Escape, Enter/Space on controls count too).
   2. Names for icon-only Zenith buttons that have none (slider arrows, back to top, close).
   3. Reduced motion: Zenith's Swiper autoplay stops; moving text stands still (CSS).
   4. Moving text: screen readers hear one copy; optional pause button (html.hom-motion-paused).
   5. HoM Motion: Soft rise, Card cascade and Photo settle while scrolling; the master switch also turns on
      Slow zoom and Drawer glide (CSS only, assets/hom-a11y.css section 3). Settings: Theme settings > HoM · Motion.
   6. Skip link: focus really lands in <main>.
   API: window.HOM.a11y = { refresh(root), reveal(el) }. */
(() => {
  const HOM = (window.HOM = window.HOM || {});
  if (HOM.a11y) return;

  const root = document.documentElement;
  const readConfig = () => {
    try {
      const el = document.getElementById('hom-a11y-config');
      return el ? JSON.parse(el.textContent) : {};
    } catch (error) {
      return {};
    }
  };
  const config = readConfig();
  const labels = Object.assign(
    { pause: 'Pause moving text', prev: 'Previous', next: 'Next', top: 'Back to top', close: 'Close' },
    config.labels || {}
  );
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motionQuery.matches;
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const store = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value === null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
      } catch (error) {
        /* private mode: the choice lasts for this page only */
      }
    },
  };

  /* 1. Keyboard focus ------------------------------------------------------------------ */
  if (config.keyboardFocus !== false) root.classList.add('hom-kbd-only');

  const NAV_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Escape', 'F6'];
  const isTyping = (target) =>
    Boolean(target) &&
    (target.isContentEditable ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      (target.tagName === 'INPUT' && !/^(button|submit|reset|checkbox|radio|range|color|file|image)$/i.test(target.type)));
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (event.key === 'Tab') {
        root.classList.add('hom-kbd');
      } else if (NAV_KEYS.includes(event.key) && !isTyping(target)) {
        root.classList.add('hom-kbd');
      } else if ((event.key === 'Enter' || event.key === ' ') && target !== document.body && !isTyping(target)) {
        root.classList.add('hom-kbd');
      }
    },
    true
  );

  /* 2. Names for icon-only controls ------------------------------------------------------ */
  const NAME_RULES = [
    { selector: '.swiper-button-prev, [class*="navigation-button-prev"], [class*="slider-nav-prev"]', label: 'prev' },
    { selector: '.swiper-button-next, [class*="navigation-button-next"], [class*="slider-nav-next"]', label: 'next' },
    { selector: '.scroll-top', label: 'top' },
    { selector: 'button[class*="close"], [role="button"][class*="close"]', label: 'close' },
  ];
  const CONTROL_SELECTOR = 'button, [role="button"], a[href]';

  const hasName = (el) => {
    if ((el.getAttribute('aria-label') || '').trim()) return true;
    const labelledBy = (el.getAttribute('aria-labelledby') || '').trim();
    if (labelledBy && labelledBy.split(/\s+/).some((id) => (document.getElementById(id)?.textContent || '').trim())) return true;
    if ((el.getAttribute('title') || '').trim()) return true;
    if ((el.textContent || '').trim()) return true;
    return Boolean(el.querySelector('img[alt]:not([alt=""]), svg title, [aria-label]:not([aria-label=""])'));
  };

  const nameControl = (el) => {
    if (el.hasAttribute('data-hom-named') || hasName(el)) return;
    const rule = NAME_RULES.find((item) => el.matches(item.selector));
    if (!rule || !labels[rule.label]) return;
    el.setAttribute('aria-label', labels[rule.label]);
    el.setAttribute('data-hom-named', '');
  };

  const nameControls = (scope) => {
    if (scope.matches && scope.matches(CONTROL_SELECTOR)) nameControl(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(CONTROL_SELECTOR).forEach(nameControl);
  };

  /* 3. Reduced motion: Swiper autoplay ---------------------------------------------------- */
  const stopAutoplay = (swiper) => {
    if (reducedMotion() && swiper && swiper.autoplay && swiper.autoplay.running && typeof swiper.autoplay.stop === 'function') {
      swiper.autoplay.stop();
    }
  };
  if (window.Swiper && typeof window.Swiper.use === 'function') {
    // Every Swiper created from now on (sections load their scripts after this file).
    const HomReducedMotion = ({ swiper, on }) => {
      on('init', () => stopAutoplay(swiper));
      on('autoplayStart', () => stopAutoplay(swiper));
    };
    window.Swiper.use([HomReducedMotion]);
  }
  const trackedSwipers = new WeakSet();
  const sweepSwipers = (scope) => {
    // Swipers created before this file ran (custom elements upgraded by global.js).
    const base = scope && scope.querySelectorAll ? scope : document;
    base.querySelectorAll('.swiper, .swiper-container, .swiper-initialized').forEach((el) => {
      const swiper = el.swiper;
      if (!swiper || trackedSwipers.has(swiper)) return;
      trackedSwipers.add(swiper);
      if (typeof swiper.on === 'function') swiper.on('autoplayStart', () => stopAutoplay(swiper));
      stopAutoplay(swiper);
    });
  };

  /* 4. Moving text ---------------------------------------------------------------------- */
  const PAUSE_KEY = 'hom:motion-paused';
  const TICKER_SELECTOR = '.announcement-bar-ticker, .ticker__wrapper';
  const MOVING_LINES =
    '.announcement-bar-ticker__line:not(.announcement-bar-ticker__line--spacer), .ticker__line:not(.ticker__line--spacer)';
  const toggles = new Set();

  const setPaused = (paused, remember) => {
    root.classList.toggle('hom-motion-paused', paused);
    toggles.forEach((button) => button.setAttribute('aria-pressed', String(paused)));
    if (remember) store.set(PAUSE_KEY, paused ? '1' : null);
  };

  const iconPause =
    '<svg class="hom-motion-toggle__pause" viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true" focusable="false"><path d="M7 4.5v11M13 4.5v11" stroke="currentColor" stroke-width="1.5"/></svg>';
  const iconPlay =
    '<svg class="hom-motion-toggle__play" viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true" focusable="false"><path d="M6.5 4.25v11.5L15.5 10l-9-5.75Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';

  const setupTicker = (ticker) => {
    if (ticker.hasAttribute('data-hom-ticker')) return;
    ticker.setAttribute('data-hom-ticker', '');

    // One readable copy: the first block of the first moving line. The rest are visual repeats.
    ticker.querySelectorAll(MOVING_LINES).forEach((line, lineIndex) => {
      Array.from(line.children).forEach((block, blockIndex) => {
        if (lineIndex === 0 && blockIndex === 0) return;
        block.setAttribute('aria-hidden', 'true');
        block.inert = true;
      });
    });

    if (config.tickerPause === false) return;
    // Zenith's speed 0: the text does not move, so there is nothing to pause.
    if (ticker.querySelector('.announcement-bar-ticker__line--stopped, .ticker__line--stopped')) return;

    // A button cannot sit inside a link. For a linked ticker, the link gets a wrapper of its own
    // size (not the whole section, whose description text would sit under the button).
    const inLink = ticker.closest('a');
    let host = ticker.closest('.announcement-bar') || ticker;
    if (inLink) {
      host = inLink.parentElement;
      if (!host) return;
      if (!host.classList.contains('hom-motion-host')) {
        const wrap = document.createElement('div');
        wrap.className = 'hom-motion-host';
        host.insertBefore(wrap, inLink);
        wrap.appendChild(inLink);
        host = wrap;
      }
    }
    if (host.querySelector(':scope > .hom-motion-toggle')) return;
    if (host !== ticker && getComputedStyle(host).position === 'static') host.style.position = 'relative';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hom-motion-toggle';
    button.setAttribute('aria-label', labels.pause);
    button.setAttribute('aria-pressed', String(root.classList.contains('hom-motion-paused')));
    button.innerHTML = iconPause + iconPlay;
    button.addEventListener('click', () => setPaused(!root.classList.contains('hom-motion-paused'), true));
    host.appendChild(button);
    toggles.add(button);
  };

  const setupTickers = (scope) => {
    if (scope.matches && scope.matches(TICKER_SELECTOR)) setupTicker(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(TICKER_SELECTOR).forEach(setupTicker);
  };

  // The visitor's earlier choice (this browser only). Ignored when the pause button is switched off.
  if (config.tickerPause !== false && store.get(PAUSE_KEY) === '1') setPaused(true, false);

  /* 5. HoM Motion ------------------------------------------------------------------------
     One calm motion language, with names so changes can be asked for by name:
       Soft rise     sections and headings fade in and rise a little, once, as they come into view (.hom-reveal)
       Card cascade  cards in a row or grid follow one another by a moment (.hom-reveal-item)
       Photo settle  photos start 2% larger and settle to their size, no blur (CSS, html.hom-settle-on)
       Slow zoom     photos zoom slowly and only a little under the mouse (CSS, html.hom-zoom-on)
       Drawer glide  drawers and pop-ups glide in on one curve, their veil fades (CSS, html.hom-motion)
     Only what is below the fold when the page opens is ever hidden: anything on screen, scrolled past or
     off to the side of a carousel shows at once, so the first screen (and the LCP image) is never delayed.
     Nothing is hidden with reduced motion, in the theme editor, in print or without this script.
     Owners: a .hom-reveal block (a House of Maryam section) or a .hom-reveal-group (an invisible trigger:
     a Zenith row such as "You may also like") starts the cascade of the cards inside it. Cards without an
     owner (collection and search grids) are watched one by one and cascade in reading order. */
  const revealMode = config.reveal || 'sections';
  const motion = Object.assign({ enabled: true, strength: 'subtle', cards: true, settle: true, hover: true }, config.motion || {});
  const motionOn = motion.enabled !== false;
  const strength = motion.strength === 'normal' ? 'normal' : 'subtle';
  // Keep in step with the tokens in assets/hom-a11y.css (--hom-dur-reveal, --hom-step, --hom-dur-settle).
  const TIMING = strength === 'normal' ? { reveal: 700, step: 80, settle: 1300 } : { reveal: 560, step: 60, settle: 1100 };
  const MAX_STEP = 7;
  const BLOCK = '.hom-reveal';
  const GROUP = '.hom-reveal-group';
  const ITEM = '.hom-reveal-item';
  const OWNER = BLOCK + ', ' + GROUP;
  // Cards that cascade. Home rows and grids, the designer rows, collection and search grids, Zenith's rows.
  const CARD_SELECTOR = [
    '.hom-carousel__slide',
    '.hom-tiles__item',
    '.hom-wall__item',
    '.hom-steps__item',
    '.hom-journal__item',
    '.hom-designer-about__item',
    '.hom-dr__item',
    '.collection-product-list > .collection-product-card',
    '.popular-products__item',
  ].join(', ');
  const GRID_LIST = '.collection-product-list';
  // Zenith sections: these rise as a whole; these have a heading that rises and cards that cascade.
  // Every other Zenith section (the product, the collection grid's toolbar, apps) never moves.
  const ZENITH_BLOCKS = '.specifications-section, .next-product-section, .content-tabs-section';
  const ZENITH_GROUPS = '.product-recommendations-section, .recently-viewed-section';

  const revealEnabled = () =>
    motionOn && revealMode !== 'off' && !reducedMotion() && !designMode() && 'IntersectionObserver' in window;
  let revealObserver = null;
  const waiting = new WeakMap(); // owner -> cards waiting for it
  const viewport = () => ({ w: window.innerWidth || root.clientWidth, h: window.innerHeight || root.clientHeight });
  const onScreen = (rect, vp) => rect.bottom > 0 && rect.top < vp.h * 0.94 && rect.right > 0 && rect.left < vp.w;

  // A card outside the screen sideways, or outside its carousel's visible strip, is not "below the fold":
  // it shows at once instead of waiting for a swipe.
  const SCROLLERS = '[data-hom-carousel-viewport], .hom-carousel__viewport, .hom-designer-about__scroller, .swiper, .slider';
  const clippedSideways = (el, rect, vp) => {
    if (rect.right <= 0 || rect.left >= vp.w) return true;
    const clip = el.parentElement && el.parentElement.closest(SCROLLERS);
    if (!clip) return false;
    const box = clip.getBoundingClientRect();
    return rect.left >= box.right - 1 || rect.right <= box.left + 1;
  };

  const showNow = (el) => {
    el.classList.add('hom-revealed', 'hom-reveal-done');
    el.style.removeProperty('--hom-i');
    if (revealObserver) revealObserver.unobserve(el);
    releaseCards(el);
  };

  // step: the card's place in its cascade (0 = at once). The CSS delay is step x --hom-step.
  const reveal = (el, step = 0) => {
    if (!el || el.classList.contains('hom-revealed')) return;
    if (step > 0) el.style.setProperty('--hom-i', String(step));
    el.classList.add('hom-revealed');
    if (revealObserver) revealObserver.unobserve(el);
    releaseCards(el, 1);
    if (!el.matches(BLOCK) && !el.matches(ITEM)) {
      el.classList.add('hom-reveal-done');
      return;
    }
    // Done once the fade and the photo settle inside it have finished (a timer: transitionend bubbles
    // up from card hovers and carousels, and a hidden element never sends one).
    const length = root.classList.contains('hom-settle-on') ? TIMING.settle : TIMING.reveal;
    window.setTimeout(() => {
      el.classList.add('hom-reveal-done');
      el.style.removeProperty('--hom-i');
    }, length + step * TIMING.step + 150);
  };

  // An owner has been revealed: the cards waiting for it cascade. Cards beside the screen (a carousel's
  // later cards) or already scrolled past show at once; cards still below the screen wait for their turn.
  // first: 1 after a block or group reveal, so the heading leads and the first card follows it by one step.
  const releaseCards = (owner, first = 0) => {
    const cards = waiting.get(owner);
    if (!cards) return;
    waiting.delete(owner);
    const vp = viewport();
    let step = first;
    cards.forEach((card) => {
      if (!card.isConnected || card.classList.contains('hom-revealed')) return;
      const rect = card.getBoundingClientRect();
      if (clippedSideways(card, rect, vp)) {
        showNow(card);
        return;
      }
      if (rect.top >= vp.h * 0.94 && revealObserver) {
        revealObserver.observe(card);
      } else if (!revealObserver || !onScreen(rect, vp)) {
        showNow(card);
      } else {
        reveal(card, Math.min(step, MAX_STEP));
        step += 1;
      }
    });
  };

  const revealAll = () => {
    document.querySelectorAll(BLOCK + ', ' + GROUP + ', ' + ITEM).forEach((el) => {
      if (!el.classList.contains('hom-revealed')) showNow(el);
    });
  };

  const isPinned = (el) => /^(fixed|sticky)$/.test(getComputedStyle(el).position);

  // A House of Maryam section: Shopify's wrapper carries the class hom-...-section (never the reveal classes).
  const isHomSection = (section) => Array.from(section.classList).some((name) => /^hom-.*-section/.test(name));

  const markSection = (section) => {
    if (section.closest('[data-hom-reveal="off"]')) return;
    if (isHomSection(section)) {
      // Every House of Maryam section in <main> (Shopify wrapper class hom-...-section), except the hero
      // (the first screen) and a section that is fixed or sticky.
      if (/\bhom-hero/.test(section.className) || section.classList.contains('hom-reveal') || isPinned(section)) return;
      section.classList.add('hom-reveal');
    } else if (section.matches(ZENITH_BLOCKS)) {
      section.classList.add('hom-reveal');
    } else if (section.matches(ZENITH_GROUPS)) {
      section.classList.add('hom-reveal-group');
      const heading = section.querySelector('.heading-group');
      if (heading) heading.classList.add('hom-reveal');
    }
  };

  const markCards = (scope) => {
    if (motion.cards === false || !scope.querySelectorAll) return;
    const cards = Array.from(scope.querySelectorAll(CARD_SELECTOR));
    if (scope.matches && scope.matches(CARD_SELECTOR)) cards.unshift(scope);
    cards.forEach((card) => {
      if (!card.closest('[data-hom-reveal="off"]')) card.classList.add('hom-reveal-item');
    });
  };

  // By setting: every section and card (as above), or only blocks that carry the class hom-reveal.
  const markScope = (scope) => {
    if (revealMode !== 'sections') return;
    const base = scope && scope.querySelectorAll ? scope : document;
    const host = base.closest ? base.closest('main .shopify-section') : null;
    const sections = host ? [host] : Array.from(base.querySelectorAll('main .shopify-section'));
    sections.forEach((section) => {
      markSection(section);
      markCards(host ? base : section);
    });
  };

  const watchReveal = (scope) => {
    if (!revealObserver) return;
    const base = scope && scope.querySelectorAll ? scope : document;
    const selector = BLOCK + ', ' + GROUP + ', ' + ITEM;
    const items = Array.from(base.querySelectorAll(selector));
    if (base.matches && base.matches(selector)) items.unshift(base);
    const fold = viewport().h;
    // Document order: an owner is always handled before the cards inside it.
    items.forEach((el) => {
      if (el.classList.contains('hom-revealed') || el.hasAttribute('data-hom-reveal-watch')) return;
      el.setAttribute('data-hom-reveal-watch', '');
      const owner = el.matches(ITEM) && el.parentElement ? el.parentElement.closest(OWNER) : null;
      if (owner && !owner.classList.contains('hom-revealed')) {
        if (!waiting.has(owner)) waiting.set(owner, []);
        waiting.get(owner).push(el);
        return;
      }
      // Already on screen, scrolled past or beside it: show at once (no fade, no flash).
      const elRect = el.getBoundingClientRect();
      if (elRect.top < fold || clippedSideways(el, elRect, viewport())) {
        showNow(el);
        return;
      }
      revealObserver.observe(el);
    });
  };

  // Cards arriving together cascade in reading order: top to bottom, then along the row.
  const rtl = () => root.getAttribute('dir') === 'rtl';
  const byReadingOrder = (a, b) => {
    const ra = a.boundingClientRect;
    const rb = b.boundingClientRect;
    if (Math.abs(ra.top - rb.top) > 8) return ra.top - rb.top;
    return rtl() ? rb.right - ra.right : ra.left - rb.left;
  };
  const onIntersect = (entries) => {
    const entering = entries.filter((entry) => entry.isIntersecting);
    entering.filter((entry) => !entry.target.matches(ITEM)).forEach((entry) => reveal(entry.target));
    entering
      .filter((entry) => entry.target.matches(ITEM))
      .sort(byReadingOrder)
      .forEach((entry, index) => reveal(entry.target, Math.min(index, MAX_STEP)));
  };

  // "Load more" and endless scroll add cards to the end of the collection grid. They are taken in here,
  // before they are first painted, so the new cards on screen cascade in like the others (never a flash).
  const takeAppendedCards = (records) => {
    if (!revealObserver || revealMode !== 'sections' || motion.cards === false) return;
    const fresh = [];
    records.forEach((record) => {
      if (!(record.target instanceof Element) || !record.target.matches(GRID_LIST)) return;
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.matches(CARD_SELECTOR) && !node.hasAttribute('data-hom-reveal-watch')) fresh.push(node);
      });
    });
    if (!fresh.length) return;
    const vp = viewport();
    let step = 0;
    fresh.forEach((card) => {
      card.classList.add('hom-reveal-item');
      card.setAttribute('data-hom-reveal-watch', '');
      const rect = card.getBoundingClientRect();
      // Below the screen, or above it (Chrome keeps the button in place and the new cards land above it):
      // they cascade when they are scrolled to.
      if (rect.top >= vp.h * 0.94 || rect.bottom <= 0) {
        revealObserver.observe(card);
      } else if (!onScreen(rect, vp)) {
        showNow(card);
      } else {
        const place = Math.min(step, MAX_STEP);
        step += 1;
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => reveal(card, place)));
      }
    });
  };

  const initReveal = () => {
    if (!revealEnabled()) return;
    markScope(document);
    revealObserver = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    watchReveal(document);
    if (motion.settle !== false) root.classList.add('hom-settle-on');
    root.classList.add('hom-reveal-on');
    // Keyboard, in-page search or print must never find hidden content: focus shows its block at once.
    document.addEventListener('focusin', (event) => {
      // Not finished = not shown yet or still fading in: either way it is completed at once.
      let hidden = event.target.closest && event.target.closest('.hom-reveal:not(.hom-reveal-done), .hom-reveal-item:not(.hom-reveal-done)');
      while (hidden) {
        showNow(hidden);
        hidden = hidden.parentElement && hidden.parentElement.closest('.hom-reveal:not(.hom-reveal-done), .hom-reveal-item:not(.hom-reveal-done)');
      }
    });
    window.addEventListener('beforeprint', revealAll);
  };

  const stopReveal = () => {
    if (revealObserver) revealObserver.disconnect();
    revealObserver = null;
    root.classList.remove('hom-reveal-on', 'hom-settle-on');
    revealAll();
  };

  // The master switch: Slow zoom and Drawer glide (CSS) follow it; the strength sets the CSS tokens.
  if (motionOn) {
    root.classList.add('hom-motion');
    root.setAttribute('data-hom-motion', strength);
    if (motion.hover !== false) root.classList.add('hom-zoom-on');
  }

  /* 5b. Frames added by apps without a name -------------------------------------------- */
  // Google's store widget (Google & YouTube app) adds an iframe with no title, so screen readers only say
  // "frame" (Lighthouse: frame-title). Name it when it appears; the watch stops after 30 seconds.
  const FRAME_NAMES = { merchantwidgetiframe: 'Google store rating' };
  const nameFrames = () => {
    let missing = false;
    Object.keys(FRAME_NAMES).forEach((id) => {
      const frame = document.getElementById(id);
      if (frame && !frame.getAttribute('title')) frame.setAttribute('title', FRAME_NAMES[id]);
      if (!frame) missing = true;
    });
    return missing;
  };
  if (nameFrames() && 'MutationObserver' in window && document.body) {
    const frameWatch = new MutationObserver(() => {
      if (!nameFrames()) frameWatch.disconnect();
    });
    frameWatch.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => frameWatch.disconnect(), 30000);
  }

  /* 6. Skip link ------------------------------------------------------------------------ */
  const initSkipLink = () => {
    const link = document.querySelector('.skip-to-content-link');
    const main = document.getElementById('MainContent');
    if (!link || !main) return;
    link.addEventListener('click', () => {
      window.setTimeout(() => {
        if (!main.contains(document.activeElement)) main.focus({ preventScroll: true });
      }, 0);
    });
  };

  /* Wiring ------------------------------------------------------------------------------- */
  const refresh = (scope = document.body) => {
    if (!scope) return;
    nameControls(scope);
    setupTickers(scope);
    sweepSwipers(scope);
    if (revealObserver) {
      markScope(scope);
      watchReveal(scope);
    }
  };

  let pending = new Set();
  let timer = 0;
  const flush = () => {
    timer = 0;
    const nodes = Array.from(pending);
    pending = new Set();
    nodes.forEach((node) => node.isConnected && refresh(node));
  };
  const observer = new MutationObserver((records) => {
    takeAppendedCards(records);
    records.forEach((record) =>
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) pending.add(node);
      })
    );
    if (pending.size && !timer) timer = window.setTimeout(flush, 200);
  });

  const onMotionChange = () => {
    if (reducedMotion()) {
      sweepSwipers(document);
      document.querySelectorAll('.swiper-initialized').forEach((el) => stopAutoplay(el.swiper));
      stopReveal();
    }
  };

  // Focus Not Obscured (WCAG 2.4.11): the height of the sticky collection toolbar, used by the page's
  // scroll padding (assets/hom-a11y.css) so a focused card is never scrolled under it.
  const watchCollectionTop = () => {
    const bar = document.querySelector('.collection-top');
    if (!bar || !('ResizeObserver' in window)) return;
    new ResizeObserver(() => {
      root.style.setProperty('--hom-collection-top-h', Math.ceil(bar.getBoundingClientRect().height) + 'px');
    }).observe(bar);
  };

  const start = () => {
    initReveal();
    refresh(document.body);
    initSkipLink();
    watchCollectionTop();
    observer.observe(document.body, { childList: true, subtree: true });
    // Swipers that initialise on window load or later.
    window.addEventListener('load', () => sweepSwipers(document), { once: true });
    if (typeof motionQuery.addEventListener === 'function') motionQuery.addEventListener('change', onMotionChange);
    document.addEventListener('shopify:section:load', (event) => {
      refresh(event.target);
      if (event.target && event.target.querySelectorAll) {
        event.target.querySelectorAll('.hom-reveal, .hom-reveal-group, .hom-reveal-item').forEach((el) => showNow(el));
      }
    });
  };

  HOM.a11y = { refresh, reveal };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
